// Real in-app guided tour overlay.
//
// Mounted once at the router root. Listens for `roava:tour:start` events,
// then guides the user through real screens with a spotlight + caption.
// Strictly presentational: never toggles a setting, writes prefs, calls
// permission APIs, or hits Supabase.
//
// Targets are real DOM elements with a `data-tour="key"` attribute.
//
// HARD GATE while a tour is active:
//   - The overlay container is `pointer-events: none` so it never swallows
//     taps by accident.
//   - 4 blocker rects around the spotlight DO opt into `pointer-events: auto`
//     and silently eat every tap outside the highlighted control.
//   - The top tour bar (Exit) and the caption (Next / Go-to) are the only
//     other tappable surfaces.
//   - The spotlight "hole" passes taps straight through to the real
//     highlighted control (Route tab, settings card, etc.).

import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { X, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { type TourStep, TOURS, type Tour } from "@/lib/onboarding/tours";
import { TOUR_START_EVENT } from "@/lib/onboarding/tour-state";
import { lockScroll, unlockScroll } from "@/lib/onboarding/scrollLock";
import { scrollTargetIntoView } from "@/lib/onboarding/scrollIntoView";

type RectLike = { top: number; left: number; width: number; height: number };

function rectsClose(a: RectLike | null, b: RectLike | null) {
  if (!a || !b) return false;
  return (
    Math.abs(a.top - b.top) < 0.5 &&
    Math.abs(a.left - b.left) < 0.5 &&
    Math.abs(a.width - b.width) < 0.5 &&
    Math.abs(a.height - b.height) < 0.5
  );
}

function findTarget(key: string | undefined): HTMLElement | null {
  if (!key || typeof document === "undefined") return null;
  const safe = (window as any).CSS?.escape ? (window as any).CSS.escape(key) : key;
  return document.querySelector(`[data-tour="${safe}"]`) as HTMLElement | null;
}

export function TourController() {
  const [tour, setTour] = useState<Tour | null>(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState<RectLike | null>(null);
  const pathname = useLocation({ select: (l) => l.pathname });
  const navigate = useNavigate();
  const stepRef = useRef<TourStep | null>(null);
  const rectRef = useRef<RectLike | null>(null);

  // Listen for start requests.
  useEffect(() => {
    const handler = (e: Event) => {
      const id = (e as CustomEvent<{ tourId: string }>).detail?.tourId;
      const next = id ? TOURS[id] : null;
      if (!next) return;
      setTour(next);
      setStepIdx(0);
    };
    window.addEventListener(TOUR_START_EVENT, handler);
    return () => window.removeEventListener(TOUR_START_EVENT, handler);
  }, []);

  const step = tour?.steps[stepIdx] ?? null;
  const total = tour?.steps.length ?? 0;
  stepRef.current = step;

  // Mark <html> so styling/QA can detect an active tour, and lock page scroll
  // for the lifetime of the tour so the user can't drift away from the target.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (tour) {
      document.documentElement.dataset.tourActive = "1";
      lockScroll();
      return () => {
        delete document.documentElement.dataset.tourActive;
        unlockScroll();
      };
    }
    delete document.documentElement.dataset.tourActive;
    return () => { delete document.documentElement.dataset.tourActive; };
  }, [tour]);

  function endTour() {
    setTour(null);
    setStepIdx(0);
    setRect(null);
    rectRef.current = null;
  }
  function nextStep() {
    if (!tour) return;
    if (stepIdx >= tour.steps.length - 1) endTour();
    else setStepIdx((i) => i + 1);
  }

  // Re-measure target. Narrow observers, throttled updates.
  useEffect(() => {
    if (!tour || !step) {
      setRect(null);
      rectRef.current = null;
      return;
    }
    const targetKey = step.target;
    let raf = 0;
    let ro: ResizeObserver | null = null;
    let mo: MutationObserver | null = null;
    let currentEl: HTMLElement | null = null;
    let lastScrolledForEl: HTMLElement | null = null;
    let recenterAttempts = 0;

    const applyRect = (next: RectLike | null) => {
      if (rectsClose(rectRef.current, next)) return;
      rectRef.current = next;
      setRect(next);
    };

    const measure = () => {
      const el = findTarget(targetKey);
      if (!el) {
        applyRect(null);
        attachObservers(null);
        return;
      }
      attachObservers(el);
      // Re-center every time we see a new target element (e.g. after a
      // route change or DOM remount), and retry a few frames in case the
      // target's final position settles after layout/transition.
      if (targetKey && (el !== lastScrolledForEl || recenterAttempts < 3)) {
        if (el !== lastScrolledForEl) {
          lastScrolledForEl = el;
          recenterAttempts = 0;
        }
        recenterAttempts++;
        scrollTargetIntoView(targetKey);
      }
      const r = el.getBoundingClientRect();
      applyRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    const scheduleMeasure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };

    const attachObservers = (el: HTMLElement | null) => {
      if (el === currentEl) return;
      currentEl = el;
      ro?.disconnect();
      ro = null;
      if (el && typeof ResizeObserver !== "undefined") {
        ro = new ResizeObserver(scheduleMeasure);
        ro.observe(el);
      }
    };

    scheduleMeasure();

    if (typeof MutationObserver !== "undefined") {
      mo = new MutationObserver(scheduleMeasure);
      mo.observe(document.body, { childList: true, subtree: true });
    }

    const onResize = scheduleMeasure;
    const onScroll = scheduleMeasure;
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);
    // #root is the real scroll container — listen to it explicitly.
    const root = document.getElementById("root");
    root?.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
      root?.removeEventListener("scroll", onScroll);
      ro?.disconnect();
      mo?.disconnect();
    };
  }, [tour, step, pathname]);

  // Tap-step advance: capture taps inside the target element.
  useEffect(() => {
    if (!tour || !step || step.kind !== "tap") return;
    const handler = (e: Event) => {
      const el = findTarget(step.target);
      if (!el) return;
      const node = e.target as Node | null;
      if (node && el.contains(node)) {
        setTimeout(() => nextStep(), 0);
      }
    };
    document.addEventListener("click", handler, true);
    document.addEventListener("pointerdown", handler, true);
    return () => {
      document.removeEventListener("click", handler, true);
      document.removeEventListener("pointerdown", handler, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour, step]);

  // Route-based auto-advance — only on real route TRANSITIONS, not on entry
  // to a step where we're already at the expected route (which would skip
  // the user's tap).
  const prevPathRef = useRef<string>(pathname);
  useEffect(() => {
    if (!tour || !step) { prevPathRef.current = pathname; return; }
    const prev = prevPathRef.current;
    prevPathRef.current = pathname;
    if (step.kind === "tap" && step.advanceOnRoute && pathname === step.advanceOnRoute && prev !== pathname) {
      nextStep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, tour, step]);

  // onEnterEvent: dispatched once whenever this step becomes active. Lets
  // a step programmatically open a sheet (e.g. GPS sheet for step 4).
  useEffect(() => {
    if (!tour || !step) return;
    const ev = (step as any).onEnterEvent as string | undefined;
    if (!ev) return;
    // Defer one frame so any preceding step-exit cleanup runs first.
    const id = window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent(ev));
    }, 0);
    return () => window.clearTimeout(id);
  }, [tour, step]);

  // advanceOnEvent: advances the tour when a named event fires (e.g. user
  // picks a GPS preset). Also dispatches onExitEvent (e.g. close sheet).
  useEffect(() => {
    if (!tour || !step) return;
    const ev = (step as any).advanceOnEvent as string | undefined;
    if (!ev) return;
    const handler = () => {
      const exitEv = (step as any).onExitEvent as string | undefined;
      if (exitEv) window.dispatchEvent(new CustomEvent(exitEv));
      nextStep();
    };
    window.addEventListener(ev, handler);
    return () => window.removeEventListener(ev, handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour, step]);

  if (!tour || !step) return null;

  const expectedRoute =
    step.kind === "info" ? step.expectRoute :
    step.kind === "tap" ? step.advanceOnRoute :
    undefined;
  const onWrongRoute = !!(expectedRoute && pathname !== expectedRoute && step.kind === "info");

  return (
    // CRITICAL: pointer-events:none on the container so the spotlight "hole"
    // is a true cut-through. Children opt back in individually.
    <div
      className="fixed inset-0 z-[60]"
      style={{ pointerEvents: "none" }}
      aria-live="polite"
      aria-label={`Guided tour: ${tour.title}`}
    >
      {/* Persistent tour bar — always tappable for Exit */}
      <div
        className="absolute inset-x-0 top-0 flex items-center justify-between gap-3 bg-foreground text-background px-3 py-2 text-xs font-medium shadow"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 8px)", pointerEvents: "auto" }}
      >
        <span className="min-w-0 flex-1 truncate">
          Tour: {tour.title} · Step {stepIdx + 1} of {total}
        </span>
        <button
          type="button"
          onClick={endTour}
          aria-label="Exit tour"
          className="shrink-0 inline-flex items-center gap-1 rounded-full bg-background/15 hover:bg-background/25 px-2.5 py-1 transition"
        >
          <X className="size-3.5" /> Exit
        </button>
      </div>

      {/* Spotlight + 4 click blockers, OR fallback dialog */}
      {rect && !onWrongRoute ? (
        <SpotlightLayer
          rect={rect}
          step={step}
          onNext={nextStep}
        />
      ) : (
        <BlockedFallback
          step={step}
          pathname={pathname}
          onNext={nextStep}
          onExit={endTour}
          onGoToRoute={
            expectedRoute && expectedRoute !== pathname
              ? () => { try { void navigate({ to: expectedRoute as any }); } catch { /* noop */ } }
              : undefined
          }
          expectedRoute={expectedRoute}
          onRecover={
            (step as any).onEnterEvent
              ? () => { try { window.dispatchEvent(new CustomEvent((step as any).onEnterEvent)); } catch { /* noop */ } }
              : undefined
          }
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Spotlight + hard-gate blockers                                     */
/* ------------------------------------------------------------------ */

function SpotlightLayer({
  rect, step, onNext,
}: { rect: RectLike; step: TourStep; onNext: () => void }) {
  const pad = 8;
  const x = Math.max(0, rect.left - pad);
  const y = Math.max(0, rect.top - pad);
  const w = rect.width + pad * 2;
  const h = rect.height + pad * 2;

  const vw = typeof window !== "undefined" ? window.innerWidth : 400;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const captionBelow = y + h + 160 < vh;
  const captionStyle: React.CSSProperties = captionBelow
    ? { top: y + h + 12, left: 16, right: 16 }
    : { bottom: vh - y + 12, left: 16, right: 16 };

  // Silent swallow: any tap on a blocker is fully consumed. preventDefault
  // also kills the synthetic mobile click that follows touchstart.
  const swallow = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const blockerBase: React.CSSProperties = {
    position: "absolute",
    pointerEvents: "auto",
    background: "transparent",
  };
  const rectTransition = "top 140ms ease, left 140ms ease, width 140ms ease, height 140ms ease, bottom 140ms ease, right 140ms ease";

  return (
    <>
      {/* Dim mask — visual only, no pointer events */}
      <svg
        className="absolute inset-0 size-full"
        aria-hidden
        style={{ pointerEvents: "none" }}
      >
        <defs>
          <mask id="roava-tour-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect x={x} y={y} width={w} height={h} rx={14} ry={14} fill="black" />
          </mask>
        </defs>
        <rect
          x="0" y="0" width="100%" height="100%"
          fill="rgba(0,0,0,0.55)"
          mask="url(#roava-tour-mask)"
        />
        <rect
          x={x} y={y} width={w} height={h} rx={14} ry={14}
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth={2}
          style={{ filter: "drop-shadow(0 0 10px hsl(var(--primary) / 0.55))" }}
        />
      </svg>

      {/* Click blockers — top / bottom / left / right of spotlight hole.
          They consume every tap that lands outside the highlighted control. */}
      <div
        onClickCapture={swallow}
        onPointerDownCapture={swallow}
        onTouchStartCapture={swallow}
        style={{ ...blockerBase, top: 0, left: 0, right: 0, height: Math.max(0, y), transition: rectTransition }}
      />
      <div
        onClickCapture={swallow}
        onPointerDownCapture={swallow}
        onTouchStartCapture={swallow}
        style={{ ...blockerBase, top: y + h, left: 0, right: 0, bottom: 0, transition: rectTransition }}
      />
      <div
        onClickCapture={swallow}
        onPointerDownCapture={swallow}
        onTouchStartCapture={swallow}
        style={{ ...blockerBase, top: y, left: 0, width: Math.max(0, x), height: h, transition: rectTransition }}
      />
      <div
        onClickCapture={swallow}
        onPointerDownCapture={swallow}
        onTouchStartCapture={swallow}
        style={{ ...blockerBase, top: y, left: x + w, right: 0, height: h, transition: rectTransition }}
      />

      {/* Caption */}
      <div
        className="absolute rounded-2xl bg-background border border-border shadow-xl p-3"
        style={{ ...captionStyle, maxWidth: 360, marginInline: "auto", pointerEvents: "auto", transition: rectTransition }}
        role="dialog"
        aria-label={step.title}
      >
        <p className="text-sm font-semibold leading-tight">{step.title}</p>
        <p className="text-xs text-muted-foreground mt-1 leading-snug">{step.body}</p>
        {step.kind === "info" && (
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={onNext}
              className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-3 py-1 text-xs font-semibold hover:opacity-90"
            >
              Next <ChevronRight className="size-3.5" />
            </button>
          </div>
        )}
        {step.kind === "tap" && (
          <p className="mt-2 text-[11px] text-muted-foreground italic">
            Tap the highlighted control to continue.
          </p>
        )}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Fallback when target is missing or user is off the expected route  */
/* ------------------------------------------------------------------ */

function BlockedFallback({
  step, pathname, onNext, onExit, onGoToRoute, expectedRoute, onRecover,
}: {
  step: TourStep;
  pathname: string;
  onNext: () => void;
  onExit: () => void;
  onGoToRoute?: () => void;
  expectedRoute?: string;
  onRecover?: () => void;
}) {
  const swallow = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const showGoTo = !!onGoToRoute && !!expectedRoute && expectedRoute !== pathname;
  return (
    <>
      <div
        className={cn("absolute inset-0 bg-black/55")}
        style={{ pointerEvents: "auto" }}
        onClickCapture={swallow}
        onPointerDownCapture={swallow}
        onTouchStartCapture={swallow}
        aria-hidden
      />
      <div
        className="absolute left-1/2 -translate-x-1/2 w-[calc(100%-32px)] max-w-sm rounded-2xl bg-background border border-border shadow-xl p-4"
        style={{ top: "40%", pointerEvents: "auto" }}
        role="dialog"
        aria-label={step.title}
      >
        <p className="text-sm font-semibold leading-tight">{step.title}</p>
        <p className="text-xs text-muted-foreground mt-1 leading-snug">{step.body}</p>
        <div className="mt-3 flex items-center justify-end gap-2 flex-wrap">
          <button
            type="button"
            onClick={onExit}
            className="rounded-full border border-border px-3 py-1 text-xs font-medium hover:bg-accent/40"
          >
            Exit
          </button>
          {showGoTo && (
            <button
              type="button"
              onClick={onGoToRoute}
              className="rounded-full border border-border px-3 py-1 text-xs font-medium hover:bg-accent/40"
            >
              Go to {expectedRoute}
            </button>
          )}
          {onRecover && (
            <button
              type="button"
              onClick={onRecover}
              className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-3 py-1 text-xs font-semibold hover:opacity-90"
            >
              Open it <ChevronRight className="size-3.5" />
            </button>
          )}
          {!onRecover && step.kind === "info" && (
            <button
              type="button"
              onClick={onNext}
              className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-3 py-1 text-xs font-semibold hover:opacity-90"
            >
              Next <ChevronRight className="size-3.5" />
            </button>
          )}
        </div>
      </div>
    </>
  );
}
